import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { RECORD_CONSTANTS } from "../../constants";
import { useRecordForm } from "./hooks/useRecordForm";
import { useRecord } from "../../hooks/useRecord";
import { scrollToSection } from "./utils/scrollToSection";
import { useCreateRecord, useUpdateRecord } from "../../hooks/useRecordMutations";
import { deleteAttachments } from "../../services/attachments/mutations";
import { createSessionId } from "../../../../services/sessionIdService";
import { useToast } from "../../../../hooks/useToast";
import { useFocusTrap } from "../../../../hooks";
import useBodyScrollLock from "../../../../hooks/useBodyScrollLock";
import { auth } from "../../../../firebase";
import EditHeader from "./components/EditHeader";
import ViewSelector from "./components/ViewSelector";
import AIAssistant from "./components/AIAssistant";
import CoreFields from "./components/CoreFields";
import AttributeSections from "./components/AttributeSections";
import AttachmentsSection from "./components/AttachmentsSection";
import LinkedRecordsSection from "./components/LinkedRecordsSection";
import VisibilitySelector from "./components/VisibilitySelector";
import styles from "./RecordEditor.module.css";
import { mergeViewsWithAttributeOverlay } from "./utils/mergeViewsWithAttributeOverlay";
import mapRecordsErrorToKey from "../../utils/recordsErrorMapper";

const toIsoString = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    return value;
  }
  if (value instanceof Date) {
    if (Number.isFinite(value.getTime())) {
      return value.toISOString();
    }
    return null;
  }
  if (typeof value.toDate === "function") {
    try {
      const dateValue = value.toDate();
      return Number.isFinite(dateValue?.getTime())
        ? dateValue.toISOString()
        : null;
    } catch (error) {
      return null;
    }
  }
  if (
    typeof value === "object" &&
    typeof value.seconds === "number" &&
    typeof value.nanoseconds === "number"
  ) {
    const millis = value.seconds * 1000 + Math.round(value.nanoseconds / 1e6);
    const dateValue = new Date(millis);
    return Number.isFinite(dateValue.getTime()) ? dateValue.toISOString() : null;
  }
  return null;
};

const parseCoordinate = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed) {
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
};

const buildAttributesSnapshot = (attributes) => {
  if (!attributes || typeof attributes !== "object") {
    return {};
  }

  const snapshot = {};
  const { date, dueDate, isComplete, location, priority } = attributes;

  if (date && typeof date === "object") {
    const start = toIsoString(date.start);
    const end = toIsoString(date.end);
    const timezone =
      typeof date.timezone === "string" && date.timezone.trim()
        ? date.timezone.trim()
        : null;

    if (start && end && timezone) {
      snapshot.date = {
        start,
        end,
        timezone,
        isAllDay: Boolean(date.isAllDay),
      };
    }
  } else if (date === null) {
    snapshot.date = null;
  }

  const dueDateIso = toIsoString(dueDate);
  if (dueDateIso) {
    snapshot.dueDate = dueDateIso;
  } else if (dueDate === null) {
    snapshot.dueDate = null;
  }

  if (typeof isComplete === "boolean") {
    snapshot.isComplete = isComplete;
  } else if (isComplete === null) {
    snapshot.isComplete = null;
  }

  if (location && typeof location === "object") {
    const lat = parseCoordinate(location.lat);
    const lng = parseCoordinate(location.lng);
    const source =
      location.source === "google" ||
      location.source === "mapbox" ||
      location.source === "manual"
        ? location.source
        : null;
    const name =
      typeof location.name === "string" && location.name.trim()
        ? location.name.trim()
        : null;

    if (
      name &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      source
    ) {
      const normalized = {
        name,
        lat,
        lng,
        source,
      };

      if (typeof location.placeId === "string" && location.placeId.trim()) {
        normalized.placeId = location.placeId.trim();
      }
      if (typeof location.context === "string" && location.context.trim()) {
        normalized.context = location.context.trim();
      }
      if (typeof location.geohash === "string" && location.geohash.trim()) {
        normalized.geohash = location.geohash.trim();
      }

      snapshot.location = normalized;
    }
  } else if (location === null) {
    snapshot.location = null;
  }

  if (
    priority === "high" ||
    priority === "medium" ||
    priority === "low"
  ) {
    snapshot.priority = priority;
  } else if (priority === null) {
    snapshot.priority = null;
  }

  return snapshot;
};

export default function RecordEditor({ record, isOpen, onClose, onSave, initialLinks }) {
  const { t } = useTranslation();
  const toast = useToast();
  const createRecord = useCreateRecord();
  const updateRecord = useUpdateRecord();
  const attachmentsSectionRef = useRef(null);
  const mapInputRef = useRef(null);
  const modalRef = useFocusTrap(isOpen, {
    initialFocus: true,
    restoreFocus: true
  });

  useBodyScrollLock(isOpen);

  // Defensive guard: Ensure we're not editing a preview record
  if (record && (record._isPreview || (!record.version && record.id))) {
    console.error("RecordEditor received a preview record:", record);
    throw new Error(
      "RecordEditor requires a full Firestore record. Received a preview. " +
      "Ensure the record is fetched from Firestore before opening the editor."
    );
  }

  // Generate sessionId for all records (for temp file uploads)
  const [sessionId] = useState(() => {
    // Always generate sessionId for both new and existing records
    // This ensures all uploads go to temp storage until save
    return createSessionId();
  });
  
  
  // Create refs for all sections
  const sectionRefs = useRef({
    [RECORD_CONSTANTS.VIEW_TYPES.TASK]: null,
    [RECORD_CONSTANTS.VIEW_TYPES.CALENDAR]: null,
    [RECORD_CONSTANTS.VIEW_TYPES.MAP]: null,
    [RECORD_CONSTANTS.VIEW_TYPES.GALLERY]: null,
    [RECORD_CONSTANTS.VIEW_TYPES.GRAPH]: null,
  });
  
  const {
    formData,
    isEditMode,
    hasChanges,
    userSelectedViews,
    ai,
    errors,
    isSaving,
    setIsSaving,
    updateField,
    handleViewClick,
    deleteAttributeSection,
    validate,
    validateField,
    computeAttachmentChanges,
    aiAssistantState,
    setAiPrompt,
    setAiStatus,
    setAiProposal,
    clearAiProposal,
    applyAiProposal,
    aiOverlay,
  } = useRecordForm(record, { initialLinks });
  const isAiSuggestionPending = aiAssistantState.status === "proposalReady";
  const wasOpenRef = useRef(isOpen);
  const linksAiOverlay = aiOverlay?.["links.outgoing"] || null;

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      if (aiAssistantState.status !== "idle") {
        setAiStatus("idle");
      }
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, aiAssistantState.status, setAiStatus]);

  // Hydrate attachments for existing records
  const {
    attachments: hydratedAttachments,
    attachmentsState,
    expectedAttachmentCount,
    attachmentPreviews
  } = useRecord(record?.id, { 
    hydrate: isEditMode && record?.id ? 'onMount' : 'none' 
  });

  // Always use form data as the source of truth for display
  const displayAttachments = formData.content?.attachments || [];

  // Track whether we've initialized attachments (one-time hydration)
  const hasInitializedAttachments = useRef(false);

  // Update form data when attachments are hydrated (initial load only)
  useEffect(() => {
    if (isEditMode && attachmentsState === 'ready' && hydratedAttachments?.length > 0 && !hasInitializedAttachments.current) {
      // Perform one-time initialization of attachments
      updateField('content.attachments', hydratedAttachments);
      // Also update the original attachments for proper deletion tracking
      updateField('_originalAttachments', hydratedAttachments);
      hasInitializedAttachments.current = true;
    }
  }, [isEditMode, attachmentsState, hydratedAttachments, updateField]);

  // Register section refs
  const registerSectionRef = useCallback((viewId) => (node) => {
    sectionRefs.current[viewId] = node;
  }, []);
  
  // Enhanced handleViewClick that includes view actions
  const handleViewClickWithActions = useCallback((view) => {
    // First call the original handleViewClick to create data if needed
    handleViewClick(view);
    
    // Check if this is a newly activated view that needs delayed scrolling
    const isNewlyActivated = !userSelectedViews.has(view);
    
    if (isNewlyActivated) {
      // Delay scroll to allow React to render the new section
      setTimeout(() => {
        scrollToSection(sectionRefs.current, view);
      }, 50); // Small delay for React to render
    } else {
      // Section already exists, scroll immediately
      scrollToSection(sectionRefs.current, view);
    }
    
    // Execute view-specific actions
    switch (view) {
      case RECORD_CONSTANTS.VIEW_TYPES.GALLERY:
        // Trigger file picker after a short delay to allow scroll
        setTimeout(() => {
          attachmentsSectionRef.current?.triggerFilePicker();
        }, 300);
        break;
      
      case RECORD_CONSTANTS.VIEW_TYPES.MAP:
        // Focus the location search input after scroll
        setTimeout(() => {
          mapInputRef.current?.focus();
        }, 300); // Allow time for scroll and render
        break;
      
      // Add future view-specific actions here
      default:
        break;
    }
  }, [handleViewClick, userSelectedViews]);
  


  // Toast duration for validation errors (auto-dismiss)
  const VALIDATION_ERROR_DURATION = 5000;

  const resolveValidationErrorMessage = useCallback(
    (errorEntry) => {
      if (!errorEntry) return "";

      if (typeof errorEntry === "string") {
        return t(errorEntry, { defaultValue: errorEntry });
      }

      if (typeof errorEntry === "object") {
        if (typeof errorEntry.i18nKey === "string" && errorEntry.i18nKey.trim()) {
          return t(errorEntry.i18nKey, {
            ...(errorEntry.values || {}),
            defaultValue: errorEntry.defaultValue || errorEntry.i18nKey,
          });
        }

        if (typeof errorEntry.message === "string" && errorEntry.message.trim()) {
          return t(errorEntry.message, { defaultValue: errorEntry.message });
        }
      }

      return t(
        "records.editor.validation.generic",
        "Please review the form fields and try again."
      );
    },
    [t]
  );

  const localizedErrors = useMemo(() => {
    if (!errors || typeof errors !== "object") {
      return {};
    }

    return Object.entries(errors).reduce((accumulator, [path, errorEntry]) => {
      accumulator[path] = resolveValidationErrorMessage(errorEntry);
      return accumulator;
    }, {});
  }, [errors, resolveValidationErrorMessage]);

  const confirmClose = useCallback(() => {
    if (!hasChanges) return true;

    return window.confirm(
      t(
        isEditMode
          ? "records.editor.confirm.discard_changes"
          : "records.editor.confirm.discard_new",
        isEditMode
          ? "Discard unsaved changes to this record?"
          : "Discard this new record?"
      )
    );
  }, [hasChanges, isEditMode, t]);

  // Helper function to handle validation errors
  const handleValidationErrors = useCallback((validationErrors) => {
    const firstError = Object.values(validationErrors)[0];
    toast.error(resolveValidationErrorMessage(firstError), VALIDATION_ERROR_DURATION);
    // Note: Field focusing is handled by the CoreFields component
  }, [toast, resolveValidationErrorMessage]);

  // Helper function to handle save errors
  const handleSaveError = useCallback((error) => {
    console.error("Save failed:", error);

    const fallbackKey = isEditMode
      ? "records.editor.errors.update_failed"
      : "records.editor.errors.create_failed";
    const errorKey = mapRecordsErrorToKey(error, { fallbackKey });
    toast.error(t(errorKey));
  }, [toast, isEditMode, t]);

  // Helper function to handle successful save (create or update)
  const handleSaveSuccess = useCallback(async (recordId, attachmentChanges, isNew) => {
    // Pass only recordId to parent, not the full record
    await onSave(recordId);

    const deletedCount = attachmentChanges.deletedCount ?? attachmentChanges.toDelete?.length ?? 0;

    if (isNew && attachmentChanges.tempAttachments?.length > 0) {
      toast.success(
        t("records.editor.toasts.created_with_migrated_files", {
          count: attachmentChanges.tempAttachments.length,
          defaultValue:
            attachmentChanges.tempAttachments.length === 1
              ? "Record created. 1 file migrated."
              : `Record created. ${attachmentChanges.tempAttachments.length} files migrated.`,
        })
      );
    } else if (!isNew && deletedCount > 0) {
      toast.success(
        t("records.editor.toasts.updated_with_deleted_files", {
          count: deletedCount,
          defaultValue:
            deletedCount === 1
              ? "Record updated and 1 attachment deleted."
              : `Record updated and ${deletedCount} attachments deleted.`,
        })
      );
    } else {
      toast.success(
        t(
          isNew
            ? "records.editor.toasts.created_success"
            : "records.editor.toasts.updated_success",
          isNew ? "Record created successfully" : "Record updated successfully"
        )
      );
    }

    onClose();
  }, [onSave, t, toast, onClose]);

  // Handle save
  const commitSave = useCallback(async () => {
    // Prevent concurrent saves
    if (isSaving || createRecord.isPending || updateRecord.isPending) {
      return;
    }

    const validationErrors = await validate();
    if (Object.keys(validationErrors).length > 0) {
      handleValidationErrors(validationErrors);
      return;
    }

    setIsSaving(true);

    try {
      if (isEditMode) {
        // Update existing record
        const updates = {
          content: formData.content,
          attributes: formData.attributes,
          access: {
            ...formData.access,
            owner: record?.access?.owner || auth.currentUser?.uid,  // Defensive guard with fallback
          },
          links: formData.links,
        };

        // Compute attachment changes before updating
        const attachmentChanges = computeAttachmentChanges();

        // Use mutation hook for updating
        const updateResult = await updateRecord.mutateAsync({
          recordId: record.id, 
          updates,
          sessionId,
          tempAttachments: attachmentChanges.tempAttachments
        });

        // Update formData with migrated attachments if any
        if (updateResult?.migratedAttachments && updateResult.migratedAttachments.length > 0) {
          // Replace temp attachments with migrated ones
          const migratedIds = new Set(updateResult.migratedAttachments.map(att => att.id));
          const nonTempAttachments = formData.content.attachments.filter(att => !att.isTemp || !migratedIds.has(att.id));
          updates.content.attachments = [...nonTempAttachments, ...updateResult.migratedAttachments];
        }

        // Handle attachment deletions separately (not handled by mutation hook)
        let attachmentDeleteResult = null;

        if (attachmentChanges.toDelete.length > 0) {
          const deleteResult = await deleteAttachments(record.id, attachmentChanges.toDelete);
          const failedDeletes = Array.isArray(deleteResult?.failed) ? deleteResult.failed : [];

          if (failedDeletes.length > 0) {
            const failedIds = new Set(failedDeletes.map(item => item.attachmentId));
            const attachmentsToRestore = (attachmentChanges.deletedAttachments || []).filter(att => failedIds.has(att.id));

            if (attachmentsToRestore.length > 0) {
              const existingIds = new Set(formData.content.attachments.map(att => att.id));
              const restored = [
                ...formData.content.attachments,
                ...attachmentsToRestore.filter(att => !existingIds.has(att.id)),
              ];

              updateField('content.attachments', restored);
            }

            const firstError = failedDeletes.find(item => item?.error)?.error;
            console.error('Attachment deletion reported partial failure', failedDeletes);
            toast.error(
              t("records.editor.errors.attachment_delete_partial", {
                count: failedDeletes.length,
                defaultValue:
                  failedDeletes.length === 1
                    ? "Failed to delete 1 attachment. Please try again."
                    : `Failed to delete ${failedDeletes.length} attachments. Please try again.`,
              })
            );
            if (firstError) {
              console.warn("Attachment deletion failure detail:", firstError);
            }
            return;
          }

          attachmentDeleteResult = deleteResult;
        }

        // Server now only returns {success, recordId}
        const recordId = updateResult?.recordId || record.id;
        const deletedCount = attachmentDeleteResult?.success?.length ?? attachmentChanges.toDelete.length;
        await handleSaveSuccess(recordId, { ...attachmentChanges, deletedCount }, false);
      } else {
        // Create new record with AI metadata if available
        const attachmentChanges = computeAttachmentChanges();

        // Use mutation hook for creating
        const createResult = await createRecord.mutateAsync({
          formData,
          ai,
          sessionId,
          tempAttachments: attachmentChanges.tempAttachments
        });

        // Server now only returns {success, recordId}
        const recordId = createResult?.recordId;
        await handleSaveSuccess(recordId, { ...attachmentChanges, deletedCount: 0 }, true);
      }
    } catch (error) {
      handleSaveError(error);
    } finally {
      setIsSaving(false);
    }
  }, [isEditMode, record, ai, validate, computeAttachmentChanges, isSaving, setIsSaving, sessionId, handleValidationErrors, handleSaveError, handleSaveSuccess, formData, createRecord, updateRecord, updateField, toast, t]);

  const handleSave = useCallback(() => {
    if (isAiSuggestionPending) {
      const shouldProceed = window.confirm(
        t(
          "records.editor.ai.confirm_save_without_apply",
          "You have an AI proposal that hasn't been applied. Save without applying it?"
        )
      );

      if (!shouldProceed) {
        toast.info(
          t(
            "records.editor.ai.apply_or_cancel_hint",
            "Please select the Cancel or Apply button in the AI Assistant section."
          )
        );
        return;
      }
    }

    commitSave();
  }, [isAiSuggestionPending, commitSave, toast, t]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        if (confirmClose()) {
          onClose();
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, confirmClose, handleSave]);

  const contentAiOverlay = useMemo(() => {
    return Object.entries(aiOverlay || {}).reduce(
      (accumulator, [path, entry]) => {
        if (path.startsWith("content.")) {
          const key = path.slice("content.".length);
          accumulator[key] = entry;
        }
        return accumulator;
      },
      {}
    );
  }, [aiOverlay]);

  const attributeAiOverlay = useMemo(() => {
    const relevantKeys = new Set([
      "date",
      "isComplete",
      "dueDate",
      "priority",
      "location",
    ]);

    return Object.entries(aiOverlay || {}).reduce(
      (accumulator, [path, entry]) => {
        if (!path.startsWith("attributes.")) {
          return accumulator;
        }

        const key = path.slice("attributes.".length);
        if (!relevantKeys.has(key)) {
          return accumulator;
        }

        accumulator[key] = entry;
        return accumulator;
      },
      {}
    );
  }, [aiOverlay]);

  const activeViewsWithOverlay = useMemo(
    () =>
      mergeViewsWithAttributeOverlay(
        userSelectedViews,
        attributeAiOverlay,
        linksAiOverlay
      ),
    [userSelectedViews, attributeAiOverlay, linksAiOverlay]
  );

  const aiSnapshot = {
    content: {
      title: formData.content?.title || "",
      body: formData.content?.body || "",
      tags: Array.isArray(formData.content?.tags)
        ? formData.content.tags
        : [],
    },
    attributes: buildAttributesSnapshot(formData.attributes),
    links: {
      outgoing: Array.isArray(formData.links?.outgoing)
        ? formData.links.outgoing
            .map((link) => {
              if (!link || typeof link !== "object") {
                return null;
              }

              const targetId =
                typeof link.targetId === "string" ? link.targetId.trim() : "";
              if (!targetId) {
                return null;
              }

              const targetType =
                link.targetType === "record" || link.targetType === "external"
                  ? link.targetType
                  : null;

              if (!targetType) {
                return null;
              }

              const addedAtIso = toIsoString(link.addedAt);
              const targetExcerpt =
                typeof link.targetExcerpt === "string" && link.targetExcerpt.trim()
                  ? link.targetExcerpt.trim()
                  : null;

              const snapshotLink = {
                targetId,
                targetType,
              };

              if (addedAtIso) {
                snapshotLink.addedAt = addedAtIso;
              }

              if (targetExcerpt) {
                snapshotLink.targetExcerpt = targetExcerpt;
              }

              return snapshotLink;
            })
            .filter(Boolean)
        : [],
    },
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className={styles.overlay} onClick={() => {
        if (confirmClose()) {
          onClose();
        }
      }} />
      <div
        className={styles.container}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-modal-title"
        ref={modalRef}
      >
        <EditHeader
          title={t(
            isEditMode
              ? "records.editor.header.edit_title"
              : "records.editor.header.create_title",
            isEditMode ? "Edit Record" : "Create Record"
          )}
          onCancel={() => {
            if (confirmClose()) {
              onClose();
            }
          }}
          onSave={handleSave}
          isDirty={hasChanges}
          isSaving={isSaving || createRecord.isPending || updateRecord.isPending}
        />

        <ViewSelector
          activeViews={activeViewsWithOverlay}
          onViewClick={handleViewClickWithActions}
        />

        <div className={styles.content}>
          <AIAssistant
            record={record}
            aiState={aiAssistantState}
            currentSnapshot={aiSnapshot}
            onPromptChange={setAiPrompt}
            onStatusChange={setAiStatus}
            onProposalReady={setAiProposal}
            onApplyProposal={applyAiProposal}
            onClearProposal={clearAiProposal}
          />

          <CoreFields
            title={formData.content.title}
            body={formData.content.body}
            tags={formData.content.tags}
            errors={localizedErrors}
            onUpdate={(field, value) => updateField(`content.${field}`, value)}
            onBlur={(field, value) => validateField(`content.${field}`, value)}
            aiOverlay={contentAiOverlay}
          />

          <div ref={registerSectionRef(RECORD_CONSTANTS.VIEW_TYPES.GALLERY)} data-scrollable-section>
            <AttachmentsSection
              ref={attachmentsSectionRef}
              attachments={displayAttachments}
              attachmentsState={attachmentsState}
              expectedCount={expectedAttachmentCount}
              previews={attachmentPreviews}
              onUpdate={(attachments) => {
                updateField("content.attachments", attachments);
              }}
              recordId={record?.id}
              sessionId={sessionId}
            />
          </div>

          <div ref={registerSectionRef(RECORD_CONSTANTS.VIEW_TYPES.GRAPH)} data-scrollable-section>
            <LinkedRecordsSection
              records={formData.links.outgoing}
              onUpdate={(records) => updateField("links.outgoing", records)}
              currentRecordId={record?.id}
              aiOverlay={linksAiOverlay}
            />
          </div>

          <VisibilitySelector
            visibility={formData.access.visibility}
            onUpdate={(visibility) => updateField("access.visibility", visibility)}
          />

          <AttributeSections
            activeViews={activeViewsWithOverlay}
            attributes={formData.attributes}
            onUpdate={(key, value) => updateField(`attributes.${key}`, value)}
            onDeleteSection={deleteAttributeSection}
            register={registerSectionRef}
            mapInputRef={mapInputRef}
            aiOverlay={attributeAiOverlay}
          />
        </div>
      </div>
    </>,
    document.body
  );
}
